import { useState, useEffect, useRef } from "react";

const cache = new Map();
const pending = new Map();

export function useGraphQL(query, variables = {}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const cacheKey =
    query + (Object.keys(variables).length ? JSON.stringify(variables) : "");

  const activeRef = useRef(true);

  useEffect(() => {
    activeRef.current = true;
    setLoading(true);
    setError(null);

    if (cache.has(cacheKey)) {
      setData(cache.get(cacheKey));
      setLoading(false);
      return;
    }

    if (!pending.has(cacheKey)) {
      const promise = fetch(process.env.REACT_APP_GRAPH_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, variables }),
      })
        .then((res) => res.json())
        .then((result) => {
          if (result.errors) throw new Error(result.errors[0].message);
          cache.set(cacheKey, result.data);
          pending.delete(cacheKey);
          return result.data;
        })
        .catch((err) => {
          pending.delete(cacheKey);
          throw err;
        });

      pending.set(cacheKey, promise);
    }

    pending
      .get(cacheKey)
      .then((result) => {
        if (!activeRef.current) return;
        setData(result);
        setLoading(false);
      })
      .catch((err) => {
        if (!activeRef.current) return;
        setError(err.message);
        setLoading(false);
      });

    return () => {
      activeRef.current = false;
    };
  }, [cacheKey]);

  const refetch = () => {
    cache.delete(cacheKey);
    pending.delete(cacheKey);
    activeRef.current = true;
    setLoading(true);
    setError(null);

    const promise = fetch(process.env.REACT_APP_GRAPH_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query, variables }),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.errors) throw new Error(result.errors[0].message);
        cache.set(cacheKey, result.data);
        pending.delete(cacheKey);
        if (activeRef.current) {
          setData(result.data);
          setLoading(false);
        }
        return result.data;
      })
      .catch((err) => {
        pending.delete(cacheKey);
        if (activeRef.current) {
          setError(err.message);
          setLoading(false);
        }
      });

    pending.set(cacheKey, promise);
  };

  return { loading, error, data, refetch };
}
